// AdminNav (DESIGN §3.2.2, N6): Kopf 68 mit Monogramm, Wortmarke und Badge „Admin“ (ohne Verlauf),
// Bereiche mit Kopf `chip` `fg-tertiary`, Einträge 36 (Icon 18, aktiv `fill`),
// Zähler rechts, Fuß mit Avatar-Zeile (AccountMenu). Liste scrollt in sich.
import { type ReactNode } from 'react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';
import { PlatformMark } from '../kern/Monogram';
import { type ShellNavItem } from './NavRail';
import { CountBadge, useRovingFocus } from './parts';

export interface AdminNavItem extends ShellNavItem {
  /** Zähler (eingeladen, Warnungen …). */
  count?: number;
  countTone?: 'neutral' | 'warning';
}

export interface AdminNavSection {
  area: string;
  items: readonly AdminNavItem[];
}

export interface AdminNavProps {
  sections: readonly AdminNavSection[];
  /** Fuß: Avatar-Zeile mit AccountMenu. */
  footer?: ReactNode;
  homeHref?: string;
  appName?: string;
  /** Nach einer Wahl (Drawer schließen). */
  onNavigate?: () => void;
  className?: string;
}

export function AdminNav({
  sections,
  footer,
  homeHref = '/',
  appName = 'Bar',
  onNavigate,
  className,
}: AdminNavProps) {
  const roving = useRovingFocus<HTMLElement>();
  return (
    <div className={cn('relative flex h-full min-h-0 flex-col bg-page', className)}>
      <div className="relative flex h-topbar shrink-0 items-center px-4">
        <RouterLink
          href={homeHref}
          aria-label={`${appName} Admin overview`}
          className="rounded-md"
          onClick={onNavigate}
        >
          <PlatformMark platform="admin" appName={appName} size={32} />
        </RouterLink>
      </div>
      <nav
        ref={roving}
        aria-label="Admin"
        className="relative min-h-0 flex-1 overflow-y-auto px-3 pb-4 [scrollbar-width:thin]"
      >
        {sections.map((section, index) => (
          <section
            key={section.area}
            aria-labelledby={`admin-area-${index}`}
            className={index === 0 ? 'mt-2' : 'mt-6'}
          >
            <h2 id={`admin-area-${index}`} className="px-3 pb-1 text-chip text-fg-tertiary">
              {section.area}
            </h2>
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const Glyph = item.icon;
                return (
                  <li key={item.id}>
                    <RouterLink
                      href={item.href}
                      reload={item.reload}
                      data-roving
                      aria-current={item.active ? 'page' : undefined}
                      onClick={onNavigate}
                      className={cn(
                        'flex h-9 items-center gap-3 rounded-md px-3 text-label font-medium transition-colors duration-(--bar-dur-fast)',
                        item.active
                          ? 'bg-card text-accent-fg'
                          : 'text-fg-secondary hover:bg-card-hover hover:text-fg',
                      )}
                    >
                      <Glyph size={18} weight={item.active ? 'fill' : 'regular'} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {item.count ? (
                        <CountBadge
                          count={item.count}
                          tone={item.countTone ?? 'neutral'}
                          cap={false}
                        />
                      ) : null}
                    </RouterLink>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
      {footer ? (
        <div className="relative shrink-0 border-t border-line-subtle p-2">{footer}</div>
      ) : null}
    </div>
  );
}
